/*
  Glooty reviews section (sections/glooty-reviews.liquid).
  1. With a reviews app: the "Write a review" button in the section head opens the app's own
     form: it looks for the app's write-review control inside the section (known selectors of
     the common apps, then any button or link that says "write a review") and clicks it. When
     the app's control cannot be reached (for example an app inside an iframe), it scrolls to
     the app instead.
  2. Sample reviews board (demo mode): All / With photos chips, the first N cards (N = phones /
     desktop setting, desktop from 990px) and a "Show more" button for the rest, the customer
     photo row jumping to its review, and a polite live status. Without JS every card shows.
*/
if (!customElements.get('glooty-reviews')) {
  const WRITE_SELECTORS = [
    '.jdgm-write-rev-link',
    '.jdgm-rev-widg__summary .jdgm-btn',
    '[data-oke-write-review]',
    '.oke-w-writeReview',
    '.yotpo-write-review-button',
    '.write-question-review-button',
    '.write-review-button',
    '.stamped-summary-actions-newreview',
    '.spr-summary-actions-newreview',
    '#loox-write-review',
    '[data-action="write-review"]',
    '[data-write-review]',
  ];
  const WRITE_TEXT = /write\s+(a\s+)?review/i;
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class GlootyReviews extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-g-write-review]');
      this.app = this.querySelector('[data-g-reviews-app]');
      if (this.button && this.app) {
        this.onClick = this.onClick.bind(this);
        this.button.addEventListener('click', this.onClick);
      }

      this.board = this.querySelector('[data-g-rev-board]');
      if (this.board) this.initBoard();
    }

    disconnectedCallback() {
      if (this.button && this.onClick) this.button.removeEventListener('click', this.onClick);
      if (this.mq && this.onMedia) this.mq.removeEventListener('change', this.onMedia);
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    /* ---------- App ---------- */

    findTrigger() {
      const known = this.app.querySelector(WRITE_SELECTORS.join(','));
      if (known) return known;
      return Array.from(this.app.querySelectorAll('button, a, [role="button"]')).find(
        (element) => element !== this.button && WRITE_TEXT.test(element.textContent || '')
      );
    }

    onClick() {
      const trigger = this.findTrigger();
      const smooth = !reduceMotion();
      if (trigger) {
        trigger.click();
        window.setTimeout(() => {
          if (!trigger.isConnected) return;
          const rect = trigger.getBoundingClientRect();
          if (rect.width && (rect.top < 0 || rect.bottom > window.innerHeight)) {
            trigger.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'center' });
          }
        }, 150);
        return;
      }
      this.app.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
      if (!this.app.hasAttribute('tabindex')) this.app.setAttribute('tabindex', '-1');
      this.app.focus({ preventScroll: true });
    }

    /* ---------- Sample reviews board ---------- */

    initBoard() {
      this.items = Array.from(this.board.querySelectorAll('[data-g-rev-item]'));
      this.chipsGroup = this.board.querySelector('[data-g-rev-chips]');
      this.chips = Array.from(this.board.querySelectorAll('[data-g-rev-filter]'));
      this.more = this.board.querySelector('[data-g-rev-more]');
      this.status = this.board.querySelector('[data-g-rev-status]');
      this.filter = 'all';
      this.expanded = false;
      this.mq = window.matchMedia('(min-width: 990px)');

      this.chips.forEach((chip) => {
        chip.addEventListener('click', () => this.setFilter(chip.dataset.gRevFilter, true));
      });
      if (this.chipsGroup) this.chipsGroup.hidden = false;

      if (this.more) {
        this.more.addEventListener('click', () => {
          const firstHidden = this.matching().find((item) => item.hidden);
          this.expanded = true;
          this.render();
          if (firstHidden) firstHidden.focus({ preventScroll: true });
        });
      }

      this.board.querySelectorAll('[data-g-rev-jump]').forEach((link) => {
        link.addEventListener('click', (event) => {
          const id = (link.getAttribute('href') || '').slice(1);
          const target = id && document.getElementById(id);
          if (!target) return;
          event.preventDefault();
          if (target.dataset.photo === 'true' && this.filter !== 'photos') this.setFilter('photos', false);
          if (target.hidden) {
            this.expanded = true;
            this.render();
          }
          target.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
          target.focus({ preventScroll: true });
        });
      });

      this.onMedia = () => this.render();
      this.mq.addEventListener('change', this.onMedia);

      // Theme editor: a selected review block is always shown
      if (window.Shopify && window.Shopify.designMode) {
        this.onBlockSelect = (event) => {
          if (!this.contains(event.target)) return;
          this.filter = 'all';
          this.expanded = true;
          this.render();
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      this.render();
    }

    limit() {
      const value = parseInt(this.mq.matches ? this.board.dataset.limitDesktop : this.board.dataset.limitMobile, 10);
      return Number.isFinite(value) && value > 0 ? value : 4;
    }

    matching() {
      return this.items.filter((item) => this.filter === 'all' || item.dataset.photo === 'true');
    }

    setFilter(filter, announce) {
      this.filter = filter === 'photos' ? 'photos' : 'all';
      this.expanded = false;
      this.render();
      if (announce && this.status) {
        const count = this.matching().length;
        const text = `Showing ${count} ${count === 1 ? 'review' : 'reviews'}${this.filter === 'photos' ? ' with photos' : ''}.`;
        this.status.textContent = '';
        window.setTimeout(() => {
          this.status.textContent = text;
        }, 50);
      }
    }

    render() {
      const matching = this.matching();
      const limit = this.expanded ? Infinity : this.limit();
      this.items.forEach((item) => {
        item.hidden = true;
      });
      matching.forEach((item, index) => {
        item.hidden = index >= limit;
      });
      if (this.more) this.more.hidden = matching.length <= limit;
      this.chips.forEach((chip) => {
        chip.setAttribute('aria-pressed', String(chip.dataset.gRevFilter === this.filter));
      });
    }
  }

  customElements.define('glooty-reviews', GlootyReviews);
}
